import type { AdminAiScanReviewProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import {
  ADMIN_AI_SCAN_SEVERITIES,
  ADMIN_AI_SCAN_SEVERITY_LABELS,
  ADMIN_AI_SCAN_TONE_CLASSES,
} from "../../utils/admin-ai-scans.constants";
import {
  getAdminAiScanBusinessObjects,
  filterAdminAiScanFindingEntries,
  getAdminAiScanFindingEntries,
  getAdminAiScanReviewModules,
  getAdminAiScanFindingSeverity,
  getAdminAiScanObjects,
  matchesAdminAiScanSearch,
} from "../../utils/admin-ai-scans.utils";
import AdminAiScanBusinessObject from "./AdminAiScanBusinessObject";
import AdminAiScanFindingModal from "./AdminAiScanFindingModal";
import AdminAiScanFindingRow from "./AdminAiScanFindingRow";
import AdminAiScanPageStatus from "./AdminAiScanPageStatus";
import AdminAiScanTreeSummary from "./AdminAiScanTreeSummary";
import { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

// Display prepared report content without choosing routes or loading another business object.
// The five review scans share the same real hierarchy, with page-owned field choices and explanations.
// The all-issues view sorts the original findings across modules without changing report coverage or source data.
const AdminAiScanReview = ({
  scan,
  title,
  description,
  featureReviewKey,
  findingFields,
  reports,
  featureReports,
  issues,
  loading,
  error,
  available,
  onRefresh,
}: AdminAiScanReviewProps) => {
  const [query, setQuery] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [severity, setSeverity] = useState("");
  const [view, setView] = useState<"modules" | "issues">("modules");
  const [selectedFindingKey, setSelectedFindingKey] = useState<string | null>(null);
  const [expansion, setExpansion] = useState<"tree" | "all" | "closed">("tree");
  const [expansionRevision, setExpansionRevision] = useState(0);
  const entries = useMemo(() => getAdminAiScanBusinessObjects(reports, featureReports), [reports, featureReports]);
  const modules = Array.from(new Set(entries.map((entry) => entry.moduleId)));
  const findings = useMemo(() => reports.flatMap((file) => getAdminAiScanObjects(file.report.findings)), [reports]);
  const findingEntries = useMemo(() => getAdminAiScanFindingEntries(entries), [entries]);
  const visibleFindings = useMemo(
    () => filterAdminAiScanFindingEntries(findingEntries, query, moduleId, severity),
    [findingEntries, query, moduleId, severity],
  );
  const visibleEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (!moduleId || entry.moduleId === moduleId) &&
          matchesAdminAiScanSearch(entry, query) &&
          (!severity ||
            entry.reports.some((file) =>
              getAdminAiScanObjects(file.report.findings).some(
                (finding) => getAdminAiScanFindingSeverity(finding) === severity,
              ),
            )),
      ),
    [entries, moduleId, query, severity],
  );
  const reviewModules = useMemo(
    () => getAdminAiScanReviewModules(visibleEntries, severity),
    [visibleEntries, severity],
  );
  const selectedFinding = visibleFindings.find((entry) => entry.key === selectedFindingKey);
  const scannedCount = entries.filter((entry) => entry.reports.length > 0).length;

  const changeExpansion = (next: typeof expansion) => {
    setExpansion(next);
    setExpansionRevision((value) => value + 1);
  };

  return (
    <section className="space-y-4" data-test={`admin-ai-${scan}-scan-page`}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <details className="mt-1 max-w-[90ch] text-sm text-gray-600 dark:text-gray-300">
            <summary className="cursor-pointer focus-visible:outline-green">About this scan</summary>
            <p className="mt-2 leading-6">{description}</p>
          </details>
        </div>
        <AdminAiScanPageStatus
          loading={loading}
          error={error}
          issues={issues}
          available={available}
          hasReports={reports.length > 0}
          onRefresh={onRefresh}
        />
      </header>
      {!loading && (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4" data-test="admin-ai-scan-metrics">
            {[
              ["Business objects", entries.length],
              ["With reports", scannedCount],
              ["Not scanned", entries.length - scannedCount],
              ["Findings", findings.length],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 bg-white px-3 py-2 shadow-sm dark:bg-gray-800"
              >
                <p className={adminAiScansStyles.muted}>{label}</p>
                <p className="text-lg font-semibold tabular-nums">{value}</p>
              </div>
            ))}
          </div>
          <div
            className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600 dark:text-gray-300"
            aria-label="Severity legend"
          >
            {ADMIN_AI_SCAN_SEVERITIES.map((level) => (
              <span
                key={level}
                className={twMerge(adminAiScansStyles.severityBadge, "gap-2", ADMIN_AI_SCAN_TONE_CLASSES[level])}
              >
                {ADMIN_AI_SCAN_SEVERITY_LABELS[level]}
                <span className="font-bold tabular-nums">
                  {findings.filter((finding) => getAdminAiScanFindingSeverity(finding) === level).length}
                </span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3" data-test="admin-ai-scan-filters">
            <label className="min-w-48 flex-1 text-xs font-semibold">
              {view === "issues" ? "Search issues" : "Search reports"}
              <input
                data-test="admin-ai-scan-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Labels, IDs, evidence, remediation…"
                className={`${adminAiScansStyles.control} mt-1 w-full`}
              />
            </label>
            <label className="text-xs font-semibold">
              Module
              <select
                data-test="admin-ai-scan-module"
                value={moduleId}
                onChange={(event) => setModuleId(event.target.value)}
                className={`${adminAiScansStyles.control} mt-1 block`}
              >
                <option value="">All modules</option>
                {modules.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold">
              Severity
              <select
                data-test="admin-ai-scan-severity"
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
                className={`${adminAiScansStyles.control} mt-1 block`}
              >
                <option value="">All severities</option>
                {ADMIN_AI_SCAN_SEVERITIES.map((level) => (
                  <option key={level} value={level}>
                    {ADMIN_AI_SCAN_SEVERITY_LABELS[level]}
                  </option>
                ))}
                <option value="unknown">Unspecified</option>
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3" data-test="admin-ai-scan-toolbar">
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Finding view"
              data-test="admin-ai-scan-view-controls"
            >
              <button
                type="button"
                className={`${adminAiScansStyles.button} aria-pressed:bg-green aria-pressed:text-white dark:aria-pressed:text-white`}
                aria-pressed={view === "modules"}
                onClick={() => setView("modules")}
                data-test="admin-ai-scan-view-modules"
              >
                By module
              </button>
              <button
                type="button"
                className={`${adminAiScansStyles.button} aria-pressed:bg-green aria-pressed:text-white dark:aria-pressed:text-white`}
                aria-pressed={view === "issues"}
                onClick={() => setView("issues")}
                data-test="admin-ai-scan-view-issues"
              >
                All issues · by severity
              </button>
            </div>
            {view === "modules" && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-test="admin-ai-scan-expand-tree"
                  className={adminAiScansStyles.button}
                  onClick={() => changeExpansion("tree")}
                >
                  Expand tree
                </button>
                <button
                  type="button"
                  data-test="admin-ai-scan-expand-findings"
                  className={adminAiScansStyles.button}
                  onClick={() => changeExpansion("all")}
                >
                  Expand findings
                </button>
                <button
                  type="button"
                  data-test="admin-ai-scan-collapse"
                  className={adminAiScansStyles.button}
                  onClick={() => changeExpansion("closed")}
                >
                  Collapse all
                </button>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300" role="status" data-test="admin-ai-scan-result-count">
            {view === "issues"
              ? `${visibleFindings.length} of ${findingEntries.length} issues · highest severity first, across modules`
              : `${visibleEntries.length} of ${entries.length} business objects`}
          </p>
          {view === "modules" && entries.length > 0 && visibleEntries.length === 0 && (
            <p role="status" className={adminAiScansStyles.panel}>
              No reports match these filters.
            </p>
          )}
          {view === "issues" ? (
            <div className={adminAiScansStyles.reportCard} data-test="admin-ai-scan-review-issues">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Issues by severity
              </div>
              <div className="space-y-2 p-3">
                {visibleFindings.length === 0 && (
                  <p role="status" className={adminAiScansStyles.panel}>
                    No issues match these filters.
                  </p>
                )}
                {visibleFindings.map(({ key, finding, source }) => (
                  <AdminAiScanFindingRow
                    key={key}
                    finding={finding}
                    source={source}
                    onOpen={() => setSelectedFindingKey(key)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div key={`modules:${expansionRevision}`} className="space-y-3" data-test="admin-ai-scan-review-modules">
              {reviewModules.map((module) => (
                <details key={module.id} open={expansion !== "closed"} className={adminAiScansStyles.reportCard}>
                  <AdminAiScanTreeSummary
                    label={module.label}
                    id={module.id}
                    meta={`${module.entries.length} business objects`}
                    counts={[{ label: "Findings", value: module.findingsCount }]}
                  />
                  <div className={adminAiScansStyles.treeChildren}>
                    {module.entries.map((entry) => (
                      <AdminAiScanBusinessObject
                        key={entry.key}
                        entry={entry}
                        featureReviewKey={featureReviewKey}
                        findingFields={findingFields}
                        expansion={expansion}
                        severity={severity}
                      />
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}
        </>
      )}
      {view === "issues" && selectedFinding && (
        <AdminAiScanFindingModal
          finding={selectedFinding.finding}
          findingFields={findingFields}
          source={selectedFinding.source}
          onClose={() => setSelectedFindingKey(null)}
        />
      )}
    </section>
  );
};

export default AdminAiScanReview;
